
import { useState, useEffect } from "react";
import { CartContext } from "@/context/CartContext";
import { CartItem, Product, CustomizationItem, ProductExtra } from "@/types/cart";
import { calculateTotalPrice, calculateTotalItems } from "@/utils/cartCalculations";
import { loadCartFromStorage, saveCartToStorage, clearCartFromStorage } from "@/utils/cartStorage";

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const loadCart = () => {
      const savedCart = loadCartFromStorage();
      setCart(savedCart);
      setIsInitialized(true);
    };
    
    loadCart();
  }, []);

  // Save cart to localStorage whenever it changes
  // But only after initial load to prevent overwriting with empty cart
  useEffect(() => {
    if (isInitialized) {
      saveCartToStorage(cart);
    }
  }, [cart, isInitialized]);

  const addToCart = (product: Product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.product.id === product.id
      );

      if (existingItem) {
        return prevCart.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { product, quantity: 1, notes: "", customizations: [], extras: [] }];
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.product.id === productId
      );

      if (existingItem && existingItem.quantity > 1) {
        return prevCart.map((item) =>
          item.product.id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      } else {
        return prevCart.filter((item) => item.product.id !== productId);
      }
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) return;

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const updateNotes = (productId: string, notes: string) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product.id === productId ? { ...item, notes } : item
      )
    );
  };
  
  const addCustomization = (productId: string, customization: CustomizationItem) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product.id === productId
          ? { ...item, customizations: [...item.customizations, customization] }
          : item
      )
    );
  };
  
  const removeCustomization = (productId: string, index: number) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product.id === productId
          ? { 
              ...item, 
              customizations: item.customizations.filter((_, i) => i !== index) 
            }
          : item
      )
    );
  };

  const updateProductExtras = (productId: string, extras: ProductExtra[]) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product.id === productId ? { ...item, extras } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    clearCartFromStorage();
  };

  const getTotalPrice = () => calculateTotalPrice(cart);
  const getTotalItems = () => calculateTotalItems(cart);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateNotes,
        addCustomization,
        removeCustomization,
        updateProductExtras,
        clearCart,
        getTotalPrice,
        getTotalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
